import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  DestroyRef,
  signal
} from '@angular/core';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { EditorEditComponent } from '../editor-edit/editor-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { EditorService } from '../editor.service';
import { EditorJsonModel } from '../editor.json.model';
import { DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditorRole } from '../editor.role.enum';
import { select, Store } from '@ngxs/store';
import { FetchOptionsActions } from '../../core/state/fetchOptions.actions';
import { FetchOptionsState } from '../../core/state/fetchOptions.state';
import { MainPaginationComponent } from '../../core/components/main-pagination/main-pagination.component';
import { AppSelectors } from '../../app.state';
import { getMinFilterLengthText } from '../../core/util';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { CollectionFilter } from '../../core/jsonApi/BaseCollectionService';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-editor',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconModule,
    MatTableModule,
    MatButtonModule,
    DatePipe,
    MatTooltip,
    MainPaginationComponent,
    FreeFilterInputComponent,
    MatFormFieldModule,
    MatSelectModule,
    TranslocoModule,
  ],
  templateUrl: './main-editor.component.html',
  styleUrl: './main-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class MainEditorComponent implements OnInit {
  private readonly store = inject(Store);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private editorService = inject(EditorService);
  private destroyRef = inject(DestroyRef);
  private nameFilter: string | undefined;
  private roleFilter: string | undefined;
  private translocoService = inject(TranslocoService);
  private localStorageService = inject(LocalStorageService);

  public displayedColumns: string[] = ['edit-actions', 'id', 'email', 'role', 'updated'];
  public dataSource = new MatTableDataSource<EditorJsonModel>([]);
  public appState = select(AppSelectors.appState);
  public readonly freeFilterLen = 3; // Min length of free filter input
  public filters: CollectionFilter[] = [];
  public currentSelectedRow = signal<string | undefined>(undefined);
  public authState = select(AppSelectors.authState);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  ngOnInit(): void {
    this.getListData();
  }

  getListData() {
    this.editorService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize),
      filters: this.filters,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const [editors] = result;
          this.dataSource = new MatTableDataSource<EditorJsonModel>(editors);
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }

  openEditDialog(editor: EditorJsonModel): void {
    this.currentSelectedRow.set(editor.id);

    const dialogRef = this.dialog.open<EditorEditComponent, EditorJsonModel, EditorJsonModel>(EditorEditComponent, {
      minWidth: '40vw',
      data: editor
    });

    dialogRef.afterClosed().subscribe(editorToSend => {
      if (editorToSend) {
        if (!editorToSend.hasId()) {
          // POST
          this.editorService.createAndSave(editorToSend).subscribe((editorFromServer) => {
            this.dataSource.data.push(editorFromServer);
            this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) + 1)); // Update paginator
          });
        }
        else {
          // PATCH
          this.editorService.update(editorToSend).subscribe((editorFromServer) => {
            this.dataSource.data = this.dataSource.data.map(item => item.id === editorFromServer.id ? editorFromServer : item);
          });
        }
        this.cdr.markForCheck();
      }
    });
  }

  onCreate() {
    this.openEditDialog(new EditorJsonModel());
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  private sortData(sort: Sort) {
    const data = this.dataSource.data.slice();

    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
    }
    else {
      this.dataSource.data = data.sort((a: EditorJsonModel, b: EditorJsonModel) => {
        const aValue = a[sort.active as keyof EditorJsonModel];
        const bValue = b[sort.active as keyof EditorJsonModel];
        return (aValue < bValue ? -1 : 1) * (sort.direction === 'asc' ? 1 : -1);
      });
    }
  }

  public confirmDelete(editor: EditorJsonModel) {
    if (this.authState().editor?.email === editor.email) {
      return;
    }

    this.currentSelectedRow.set(editor.id);

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: editor.id,
          name: editor.email,
          itemType: 'Benutzer',
          deleteFn: () => {
            return editor.destroy();
          }
        },
      });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: boolean) => {
      if (result) {
        this.dataSource.data = this.dataSource.data.filter(value => value.id !== editor.id);
        this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) - 1)); // Update paginator
      }
    });
  }

  paginationChanged = (): void => {
    this.getListData();
  };

  isValidDate(dateString: string | Date): boolean {
    if (!dateString) {
      return false; // Kein Datum vorhanden
    }
    const date = new Date(dateString);
    // Überprüft, ob das Datum gültig ist und nicht 'Invalid Date' darstellt
    return date instanceof Date && !isNaN(date.getTime());
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setNameFilter(ev: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.nameFilter = (ev.length === 0 ? undefined : ev);
    this.setFilters();
  }

  public setRoleFilter(ev: string): void {
    this.currentSelectedRow.set(undefined); // unmark the select row
    this.roleFilter = (ev.length === 0 ? undefined : ev);
    this.setFilters();
  }

  // Set view filter
  public setFilters() {
    this.filters = [];

    // E-Mail filter
    if (this.nameFilter) {
      this.filters.push({ fieldName: 'email', value: this.nameFilter });
    }

    // Role filter
    if (this.roleFilter) {
      this.filters.push({ fieldName: 'role', value: this.roleFilter });
    }

    this.store.dispatch(new FetchOptionsActions.UpdateFilters(this.filters)); // Update filter
    this.getListData();
  }

  // simple view accessors
  // protected readonly getEditorRoleViewValue = getEditorRoleViewValue;
  public getEditorRole(role: EditorRole): string {
    return this.translocoService.translate(role);
  }
}
